import { apiClient } from './api'
import type { User } from '@/stores/auth'

interface AuthResponse {
  success: boolean
  user?: User
  token?: string
  message?: string
}

interface VerifyResponse {
  success: boolean
  user?: User
  message?: string
}

class AuthService {
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password
      })
      
      return {
        success: true,
        user: response.data.user,
        token: response.data.access_token // Backend sends "access_token", not "token"
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      }
    }
  }

  async register(name: string, email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/register', {
        name,
        email,
        password
      })
      
      return {
        success: true,
        user: response.data.user,
        token: response.data.access_token // Backend sends "access_token", not "token"
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
      // Don't throw - logout should always succeed locally
    }
  }

  async verifyToken(): Promise<VerifyResponse> {
    try {
      const response = await apiClient.get('/auth/me')
      
      return {
        success: true,
        user: response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Token verification failed'
      }
    }
  }

  async getCurrentUser(): Promise<VerifyResponse> {
    try {
      const response = await apiClient.get('/user')
      
      return {
        success: true,
        user: response.data.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get user data'
      }
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/refresh')
      
      return {
        success: true,
        user: response.data.user,
        token: response.data.access_token // Backend sends "access_token", not "token"
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Token refresh failed'
      }
    }
  }
}

export const authService = new AuthService()